/**
 * Onboarding step 2 of 2 (Figma `354:13640`), where the organisation is created ("A organização é
 * criada agora"): step 1 only named it. Both actions create it and land on the tenant home; the
 * only difference is whether capacity requests go with it, so asking for capacity never blocks
 * the sign-up ("sem travar o cadastro").
 *
 * "Criar organização" always sends one `kind: 'SLOTS'` request with the slots amount, and a
 * `kind: 'CREDITS'` request only when a credit option is selected ("Outro" asks for an
 * unspecified amount, `null`). "Criar sem pedir agora" creates the organisation with no request.
 */
import { useEffect, useId, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { Activity } from 'lucide-react';
import { useNavigate } from '@tanstack/react-router';

import { getLocale } from '../../../i18n';
import { integer } from '../../../i18n/format';
import { Alert, AlertDescription } from '../../../ui/alert';
import { Button } from '../../../ui/button';
import { Card, CardContent } from '../../../ui/card';
import { SEGMENTED_ITEM_CLASSNAME, ToggleGroup, ToggleGroupItem } from '../../../ui/toggle-group';
import type { CapacityRequestInput } from '../capacity/api/schemas';
import { tt } from '../i18n';
import { PageRoot } from './PageRoot';
import { useCreateTenant } from './useCreateTenant';

const SLOT_MIN = 0;
const CREDIT_OPTIONS = [20_000, 50_000, 120_000] as const;
/** The toggle group's value for "Outro"; `''` is its value with nothing selected. */
const OTHER = 'other';

export function Capacity(props: { organisationName: string }): ReactNode {
  const slotsId = useId();
  const creditsId = useId();
  const locale = getLocale();
  const navigate = useNavigate();
  const { mutation, created } = useCreateTenant();

  const [slots, setSlots] = useState(2);
  const [credits, setCredits] = useState('');
  const heading = useRef<HTMLHeadingElement>(null);

  // This step replaces step 1 in place, taking the focused button with it; without this, focus
  // falls back to the page and a keyboard or screen-reader user starts over from the top.
  useEffect(() => heading.current?.focus(), []);

  function create(requests: CapacityRequestInput[]): void {
    mutation.mutate(
      { name: props.organisationName, requests },
      { onSuccess: (tenant) => void navigate({ to: `/t/${tenant.id}/creditos` }) },
    );
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const context = tt('onboarding.capacity.context');
    const requests: CapacityRequestInput[] = [
      {
        kind: 'SLOTS',
        slots_requested: slots,
        credits_requested: null,
        accounts_to_track: null,
        context,
      },
    ];
    if (credits !== '') {
      requests.push({
        kind: 'CREDITS',
        slots_requested: null,
        credits_requested: credits === OTHER ? null : Number(credits),
        accounts_to_track: null,
        context,
      });
    }
    create(requests);
  }

  return (
    <PageRoot>
      <Card className="w-full">
        <CardContent className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <span className="flex size-7 items-center justify-center rounded-md bg-primary text-primary-foreground">
              <Activity aria-hidden className="size-4" />
            </span>
            {/* eslint-disable-next-line no-restricted-syntax -- the product's name, not copy: it does not change with locale */}
            <span className="text-base font-semibold text-foreground">SocialSrc</span>
          </div>

          <div className="flex items-center gap-2">
            <span aria-hidden className="flex items-center gap-1.5">
              <span className="h-1 w-2 rounded-full bg-border" />
              <span className="h-1 w-6 rounded-full bg-primary" />
            </span>
            <span className="text-xs font-medium text-muted-foreground">
              {tt('onboarding.capacity.stepLabel')}
            </span>
          </div>

          <h1
            ref={heading}
            tabIndex={-1}
            className="text-2xl font-semibold text-foreground outline-none"
          >
            {tt('onboarding.capacity.title', { organisation: props.organisationName })}
          </h1>
          <p className="text-sm text-muted-foreground">{tt('onboarding.capacity.lead')}</p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <span id={slotsId} className="text-sm font-medium text-foreground">
                {tt('onboarding.capacity.slots.label')}
              </span>
              <div
                role="group"
                aria-labelledby={slotsId}
                className="inline-flex w-fit items-center rounded-md border"
              >
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  disabled={slots <= SLOT_MIN}
                  aria-label={tt('onboarding.capacity.slots.decrease')}
                  onClick={() => setSlots((current) => Math.max(SLOT_MIN, current - 1))}
                  // eslint-disable-next-line no-restricted-syntax -- decorative glyph: the button's own aria-label above carries the accessible name
                >
                  −
                </Button>
                <span data-testid="capacity-slots-value" className="w-10 text-center text-sm">
                  {integer(slots, locale)}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={tt('onboarding.capacity.slots.increase')}
                  onClick={() => setSlots((current) => current + 1)}
                  // eslint-disable-next-line no-restricted-syntax -- decorative glyph: the button's own aria-label above carries the accessible name
                >
                  +
                </Button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <span id={creditsId} className="text-sm font-medium text-foreground">
                {tt('onboarding.capacity.credits.label')}
              </span>
              <ToggleGroup
                type="single"
                spacing={1}
                aria-labelledby={creditsId}
                value={credits}
                onValueChange={setCredits}
                className="w-full flex-wrap rounded-md bg-muted p-1"
              >
                {CREDIT_OPTIONS.map((option) => (
                  <ToggleGroupItem
                    key={option}
                    value={String(option)}
                    className={SEGMENTED_ITEM_CLASSNAME}
                  >
                    {tt('onboarding.capacity.credits.option', {
                      amount: integer(option / 1000, locale),
                    })}
                  </ToggleGroupItem>
                ))}
                <ToggleGroupItem value={OTHER} className={SEGMENTED_ITEM_CLASSNAME}>
                  {tt('onboarding.capacity.credits.other')}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>

            {mutation.isError && (
              <Alert variant="destructive">
                {/* Once the organisation exists only the request is left to retry; before it, a
                    404 means `POST /tenants` is unreachable, which retrying cannot fix. */}
                <AlertDescription>
                  {created !== null
                    ? tt('onboarding.capacity.failed')
                    : mutation.error.failure.kind === 'not-found'
                      ? tt('onboarding.unavailable')
                      : tt('onboarding.failed')}
                </AlertDescription>
              </Alert>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
              <Button
                type="button"
                variant="ghost"
                className="font-normal text-muted-foreground"
                disabled={mutation.isPending}
                onClick={() => create([])}
              >
                {tt('onboarding.capacity.skip')}
              </Button>
              <Button type="submit" disabled={mutation.isPending}>
                {tt('onboarding.capacity.submit')}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </PageRoot>
  );
}
